import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { createContext, ReactNode, useRef, useState } from 'react';

import { ImmersivePermissions } from '../../model/permissions.enum';
import { ERROR_TYPE, ImmersiveError } from '../../utils/error';
import { ImmersiveAuthentication } from '../model/authentication';
import {
  AuthFailureStep,
  describeAuthFailure,
  recordAuthFailure,
} from './auth-failure';
import { createReauthGuard, ReauthOutcome } from './reauth-guard';

interface IAuthParamsProvider {
  children?: ReactNode;
}

interface IAuthContext {
  login: (token: string) => Promise<any>;
  reauth: () => Promise<ReauthOutcome>;
  ensureImmersiveSession: () => Promise<ReauthOutcome>;
  accessToken: string | null;
  setToken: (props: string) => void;
  iLogout: () => void;
  getPermissions: () => void;
  permissions: string[];
  setPermissions: (props: ImmersivePermissions[]) => void;
}

// Cooldown after a failed re-exchange before we try again. Prevents hammering
// getAccessTokenSilently / /public/auth0_authenticate on an expired session.
const REAUTH_BACKOFF_MS = 10000;

const AuthContext = createContext({} as IAuthContext);

export const AuthContextProvider = (props: IAuthParamsProvider) => {
  const BASE_URL =
    import.meta.env.REACT_APP_AUTH_DOMAIN ?? 'https://auth.test.immersive-finance.com';
  const [permissions, setPermissions] = useState<string[]>([]);
  const [accessToken, setToken] = useState('');

  const { getAccessTokenSilently } = useAuth0();
  const reauthGuardRef = useRef(createReauthGuard({ backoffMs: REAUTH_BACKOFF_MS }));

  const login = async (token: string) => {
    if (token) {
      const data = {
        auth0_token: token,
      };

      const config = {
        method: 'post',
        url: BASE_URL + '/public/auth0_authenticate',
        data: data,
        headers: {
          'Content-Type': 'application/json',
        },
      };

      return axios(config)
        .then((response) => {
          if (response.status === 200) {
            const immersiveResponse = response.data as ImmersiveAuthentication;
            if (immersiveResponse.permissions) {
              setPermissions(immersiveResponse.permissions);
              setToken(immersiveResponse.access_token);

              return {
                data: {
                  message: 'Successfully Logged In. Redirecting...',
                  msgError: false,
                  permissions: immersiveResponse.permissions,
                },
              };
            } else {
              throw new ImmersiveError({
                name: ERROR_TYPE.AUTHENTICATION_ERROR,
                message: 'User has no permission to access this system',
                cause: 'No authorized',
              });
            }
          } else {
            throw new ImmersiveError({
              name: ERROR_TYPE.AUTHENTICATION_ERROR,
              message: 'User has no permission to access this system',
              cause: 'No authorized',
            });
          }
        })
        .catch((err) => {
          // NB: axios rejects with the error OBJECT, not `{ error }` — the old
          // destructure lost the reason entirely (message came out undefined),
          // which is a big part of why the beta CORS outage was invisible.
          // Classify from the real error so the message carries the cause.
          const info = describeAuthFailure(err, 'exchange');
          throw new ImmersiveError({
            name: ERROR_TYPE.AUTHENTICATION_ERROR,
            message: info.message,
            cause: info.code ?? String(info.status ?? ''),
          });
        });
    }
  };
  const getPermissions = () => {
    if (accessToken) {
      const config = {
        method: 'get',
        url: BASE_URL + '/private/verify_token',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      return axios(config)
        .then((response) => {
          return response.data.permissions;
        })
        .catch(function (error) {
          console.log(error);
          return null;
        });
    }
  };
  const iLogout = () => {
    if (accessToken) {
      const config = {
        method: 'get',
        url: BASE_URL + '/private/logout',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      return axios(config).then(() => {
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('permissions');
      });
    }
  };

  // Single guarded re-exchange: Auth0 silent token -> POST /public/auth0_authenticate.
  // Returns 'ok' when a fresh Immersive JWT + permissions were obtained, else
  // 'no-session' (Auth0 session gone or backend rejected) so the caller can send
  // the user to Auth0. The guard dedupes concurrent callers and backs off after
  // failure, so this is safe to call from the protected-route init, dashboard,
  // and (once the WS auth-failure signal is confirmed) the WS layer.
  const exchangeImmersiveSession = async (): Promise<ReauthOutcome> => {
    // Track which leg we're on so a thrown error is attributed correctly.
    let step: AuthFailureStep = 'silent-token';
    try {
      const auth0Token = await getAccessTokenSilently({ cacheMode: 'off' });
      step = 'exchange';
      const response = await login(auth0Token);
      const ok = !!response?.data?.permissions?.length;
      if (!ok) {
        // 200 but empty permissions — the exchange "worked" yet grants nothing.
        recordAuthFailure(
          describeAuthFailure({ kind: 'no-permissions', host: BASE_URL }, 'exchange'),
        );
      }
      return ok ? 'ok' : 'no-session';
    } catch (err) {
      // Record the classified reason (console + sessionStorage) BEFORE returning
      // 'no-session'. Behaviour is unchanged — the guard still backs off and the
      // caller still sends the user to Auth0 — but now the reason is visible
      // instead of a silent bounce (IM-2017).
      recordAuthFailure(describeAuthFailure(err, step));
      return 'no-session';
    }
  };

  const ensureImmersiveSession = (): Promise<ReauthOutcome> =>
    reauthGuardRef.current.run(exchangeImmersiveSession);

  // Kept for the WS auth-failure path (IM-1991 fix #2). Delegates to the same
  // guarded routine so it can never loop; wiring the trigger is pending the
  // backend auth-failure signal.
  const reauth = (): Promise<ReauthOutcome> => ensureImmersiveSession();

  return (
    <>
      <AuthContext.Provider
        value={{
          getPermissions,
          login,
          iLogout,
          permissions,
          setPermissions,
          accessToken,
          setToken,
          reauth,
          ensureImmersiveSession,
        }}
      >
        {props.children}
      </AuthContext.Provider>
    </>
  );
};

export default AuthContext;
